import { takeLatest, select, put, call } from 'redux-saga/effects';
import { checkToken } from '../auth/sagas';
import { Urls } from '../../config/urls';
import { patchTask, postTask } from '../../services';
import {
  ProfileActionType,
  savePersonalInfoSuccess,
  savePersonalInfoError,
  changePasswordSuccess,
  changePasswordError,
  setupTwoFactorSuccess,
  setupTwoFactorError,
  confirmTwoFactorSuccess,
  confirmTwoFactorError,
  disableTwoFactorSuccess,
  disableTwoFactorError,
  updateNotificationPreferencesSuccess,
  updateNotificationPreferencesError,
} from './actions';
import { PanelStore } from '../interfaces';
import { updateUserMe } from '../dashboard/actions';

function* savePersonalInfo(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { name, language, marketingConsent } = action;
  const { response, error } = yield call(patchTask, {
    url: Urls.api.userUpdate,
    body: { name, language, marketingConsent },
    token: state.auth.token,
  });

  if (response) {
    yield put(savePersonalInfoSuccess(response));
  } else {
    yield put(savePersonalInfoError(error));
  }
}

export function* savePersonalInfoSaga() {
  yield takeLatest(ProfileActionType.SAVE_PERSONAL_INFO, savePersonalInfo);
}

function* changePassword(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { oldPassword, newPassword } = action;
  const { response, error } = yield call(patchTask, {
    url: Urls.api.changePassword,
    body: { oldPassword, newPassword },
    token: state.auth.token,
  });

  if (response) {
    yield put(changePasswordSuccess(response));
  } else {
    yield put(changePasswordError(error));
  }
}

export function* changePasswordSaga() {
  yield takeLatest(ProfileActionType.CHANGE_PASSWORD, changePassword);
}

function* setupTwoFactor() {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(postTask, {
    url: Urls.api.twoFactorSetup,
    body: {},
    token: state.auth.token,
  });

  if (response) {
    yield put(setupTwoFactorSuccess(response));
  } else {
    yield put(setupTwoFactorError(error));
  }
}

export function* setupTwoFactorSaga() {
  yield takeLatest(ProfileActionType.TWO_FACTOR_SETUP, setupTwoFactor);
}

function* confirmTwoFactor(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { code } = action;
  const { response, error } = yield call(postTask, {
    url: Urls.api.twoFactorConfirm,
    body: { code },
    token: state.auth.token,
  });

  if (response) {
    yield put(confirmTwoFactorSuccess());
    yield put(updateUserMe({ totpEnabled: true, totpConfigured: true }));
  } else {
    yield put(confirmTwoFactorError(error));
  }
}

export function* confirmTwoFactorSaga() {
  yield takeLatest(ProfileActionType.TWO_FACTOR_CONFIRM, confirmTwoFactor);
}

function* disableTwoFactor(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { code } = action;
  const { response, error } = yield call(postTask, {
    url: Urls.api.twoFactorDisable,
    body: { code },
    token: state.auth.token,
  });

  if (response) {
    yield put(disableTwoFactorSuccess());
    yield put(updateUserMe({ totpEnabled: false, totpConfigured: false }));
  } else {
    yield put(disableTwoFactorError(error));
  }
}

export function* disableTwoFactorSaga() {
  yield takeLatest(ProfileActionType.TWO_FACTOR_DISABLE, disableTwoFactor);
}

function* updateNotificationPreferences(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { notificationPreferences, onSuccess, onError } = action;
  const { response, error } = yield call(patchTask, {
    url: Urls.api.userUpdate,
    body: { notificationPreferences },
    token: state.auth.token,
  });

  if (response) {
    yield put(updateNotificationPreferencesSuccess());
    yield put(updateUserMe({ notificationPreferences }));
    if (typeof onSuccess === 'function') {
      onSuccess();
    }
  } else {
    yield put(updateNotificationPreferencesError(error));
    if (typeof onError === 'function') {
      onError();
    }
  }
}

export function* updateNotificationPreferencesSaga() {
  yield takeLatest(ProfileActionType.UPDATE_NOTIFICATION_PREFERENCES, updateNotificationPreferences);
}
